import React, { useEffect, useRef, useState } from "react";
import { Eraser, Loader2 } from "lucide-react";

/**
 * Lightweight mouse/touch signature pad. Emits PNG data URL on save.
 * Props: onSubmit(dataUrl), disabled, submitLabel
 */
export default function SignaturePad({ onSubmit, disabled, submitLabel = "Signer et générer le contrat" }) {
    const canvasRef = useRef(null);
    const [empty, setEmpty] = useState(true);
    const [saving, setSaving] = useState(false);
    const drawing = useRef(false);
    const last = useRef({ x: 0, y: 0 });

    // Setup high-DPI canvas
    useEffect(() => {
        const cv = canvasRef.current;
        const ratio = window.devicePixelRatio || 1;
        const w = cv.offsetWidth;
        const h = cv.offsetHeight;
        cv.width = w * ratio;
        cv.height = h * ratio;
        const ctx = cv.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#0B192C";
    }, []);

    const pos = (e) => {
        const cv = canvasRef.current;
        const rect = cv.getBoundingClientRect();
        if (e.touches && e.touches[0]) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top,
            };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        last.current = pos(e);
    };

    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const p = pos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last.current = p;
        if (empty) setEmpty(false);
    };

    const stop = () => (drawing.current = false);

    const clear = () => {
        const cv = canvasRef.current;
        cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
        setEmpty(true);
    };

    const submit = async () => {
        if (empty) return;
        setSaving(true);
        const dataUrl = canvasRef.current.toDataURL("image/png");
        try {
            await onSubmit(dataUrl);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid="signature-pad">
            <div className="flex items-center justify-between mb-2">
                <div className="overline">Signature électronique — tracez avec votre souris</div>
                <button
                    type="button"
                    onClick={clear}
                    className="text-xs text-slate-500 hover:text-red-600 font-semibold inline-flex items-center gap-1"
                    data-testid="signature-clear-btn"
                >
                    <Eraser size={14} /> Effacer
                </button>
            </div>
            <canvas
                ref={canvasRef}
                className="w-full h-40 bg-slate-50 rounded-lg border border-dashed border-slate-300 cursor-crosshair touch-none"
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={stop}
                onMouseLeave={stop}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={stop}
                data-testid="signature-canvas"
            />
            <button
                type="button"
                onClick={submit}
                disabled={empty || saving || disabled}
                className="btn-gold w-full mt-3 text-sm disabled:opacity-40"
                data-testid="signature-submit-btn"
            >
                {saving ? <Loader2 size={16} className="inline animate-spin" /> : submitLabel}
            </button>
        </div>
    );
}
